import React, { useCallback, useState, useEffect, Fragment } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { isBlank, notBlank } from "../util/ext";
import { none, equals, map, lensPath, set, reject } from "ramda";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import MediaDialog from "../creator-application/common/MediaDialog";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import TextField from "@mui/material/TextField";
import ModuleAutoComplete from "./ModuleAutoComplete";
import { useModules, useModuleGroupCreate } from "./moduleGroupsService";

const useStyles = makeStyles(theme => ({
  form: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(0, 2),
  },

  tableContainer: {
    maxHeight: "300px",
    marginBottom: theme.spacing(10),
    padding: theme.spacing(0, 2),
  },

  title: {
    color: theme.palette.primary.brandPrimary,
  },

  errorContainer: {
    marginTop: theme.spacing(1),
    fontWeight: "bold",
    color: theme.palette.error.main,
  },
}));

const initialModuleGroup = {
  moduleGroupName: "",
  modules: [],
};

const initialError = {
  error: false,
  helperText: "",
};

const NewModuleGroupDialog = props => {
  const { open, onCancel, onOk } = props;
  const classes = useStyles();

  const modules = useModules();

  const [moduleGroup, setModuleGroup] = useState(initialModuleGroup);
  const [moduleList, setModuleList] = useState([]);
  const [moduleAutoCompleteList, setModuleAutoCompleteList] = useState(modules);
  const [selectedModule, setSelectedModule] = useState({});
  const [disabledModules, setDisabledModules] = useState([]);
  const [errors, setErrors] = useState(initialError);

  useEffect(() => {
    if (modules.isSuccess) {
      const mappedModules = map(module => {
        return {
          moduleName: module.name,
          moduleId: module.id,
        };
      }, modules.data);
      console.log(mappedModules);
      setModuleAutoCompleteList(mappedModules);
    }
  }, [modules.isSuccess, modules.data]);

  const createModuleGroup = useModuleGroupCreate();

  const handleApply = useCallback(async () => {
    try {
      moduleGroup.modules = moduleList;
      const newModuleGroup = await createModuleGroup.mutateAsync({
        data: moduleGroup,
      });
      setModuleGroup(initialModuleGroup);
      setModuleList([]);
      setErrors(initialError);
      onOk(newModuleGroup.id);
    } catch (e) {
      setErrors({ error: true, helperText: "Please try a different name." });
    }
  }, [createModuleGroup, moduleGroup, moduleList, onOk]);

  const handleClose = useCallback(() => {
    setErrors(initialError);
    setModuleGroup(initialModuleGroup);
    setModuleList([]);
    setDisabledModules([]);
    onCancel();
  }, [onCancel]);

  const handleTextChange = path => event => {
    const value = notBlank(event.target) ? event.target.value : event;
    setModuleGroup(prev => set(lensPath(path), value, prev));
  };

  const handleAddModule = () => {
    if (isBlank(selectedModule)) {
      return;
    }

    if (
      none(module => equals(module.moduleId, selectedModule.moduleId))(
        moduleList,
      )
    ) {
      setModuleList(prevList => [...prevList, selectedModule]);
      setDisabledModules(prevList => [...prevList, selectedModule]);
    }
  };

  const handleRemoveModule = moduleId => {
    const newModules = reject(
      module => equals(module.moduleId, moduleId),
      moduleList,
    );

    setModuleList(newModules);
    setDisabledModules(newModules);
  };

  return (
    <MediaDialog
      fullWidth
      maxWidth="lg"
      open={open}
      title="New Module Group"
      handleClose={handleClose}
      loading={false}
      content={
        <Fragment>
          <Box className={classes.form}>
            <Grid spacing={2} container>
              <Grid item xs={12}>
                <TextField
                  label="Module Group Name"
                  fullWidth
                  value={moduleGroup.moduleGroupName}
                  onChange={handleTextChange(["moduleGroupName"])}
                  error={errors.error}
                  helperText={errors.helperText}
                />
              </Grid>

              <Grid container item xs={12} alignItems="flex-end">
                <Grid item xs={10}>
                  <ModuleAutoComplete
                    label="Module"
                    modules={moduleAutoCompleteList}
                    disabledModules={disabledModules}
                    onChange={(event, val) => setSelectedModule(val)}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    style={{ padding: "0 6px", float: "right" }}
                    onClick={handleAddModule}
                  >
                    Add Module
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Box>

          <TableContainer classes={{ root: classes.tableContainer }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell key="module-id" style={{ paddingLeft: 0 }}>
                    Module ID
                  </TableCell>
                  <TableCell key="module-name" style={{ paddingLeft: 0 }}>
                    Module Name
                  </TableCell>
                  <TableCell key="remove" align="center">
                    Remove
                  </TableCell>
                </TableRow>
              </TableHead>
              {moduleList.length > 0 && (
                <TableBody>
                  {moduleList.map(({ moduleId, moduleName }) => (
                    <TableRow key={moduleId} hover tabIndex={-1}>
                      <TableCell key={"id:" + moduleId}>{moduleId}</TableCell>
                      <TableCell key={"name:" + moduleId}>
                        {moduleName}
                      </TableCell>
                      <TableCell key={"remove:" + moduleId} align="center">
                        <IconButton
                          onClick={() => handleRemoveModule(moduleId)}
                          size="large"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Fragment>
      }
      actions={
        <Button
          onClick={handleApply}
          variant="contained"
          disabled={
            moduleList.length === 0 || moduleGroup.moduleGroupName === ""
              ? true
              : false
          }
        >
          Create
        </Button>
      }
    />
  );

  // return (
  //   <Dialog
  // fullWidth
  // maxWidth="lg"
  //     open={open}
  //     onClose={handleClose}
  //     aria-labelledby="new-module-group-dialog-title"
  //     classes={{ root: classes.dialog }}
  //   >
  //     <DialogTitle id="new-module-group-dialog-title" className={classes.title}>
  //       New Module Group
  //     </DialogTitle>
  //     <DialogContent>
  //       <Box className={classes.form}>
  //         <Grid spacing={2} container>
  //           <Grid item xs={12}>
  //             <TextField
  //               label="Module Group Name"
  //               fullWidth
  //               value={moduleGroup.moduleGroupName}
  //               onChange={handleTextChange(["moduleGroupName"])}
  //               error={errors.error}
  //               helperText={errors.helperText}
  //             />
  //           </Grid>

  //           <Grid container item xs={12} alignItems="flex-end">
  //             <Grid item xs={10}>
  //               <ModuleAutoComplete
  //                 label="Module"
  //                 modules={moduleAutoCompleteList}
  //                 disabledModules={disabledModules}
  //                 onChange={(event, val) => setSelectedModule(val)}
  //               />
  //             </Grid>
  //             <Grid item xs={2}>
  //               <Button
  //                 style={{ padding: "0 6px", float: "right" }}
  //                 onClick={handleAddModule}
  //               >
  //                 Add Module
  //               </Button>
  //             </Grid>
  //           </Grid>
  //         </Grid>
  //       </Box>

  // <TableContainer>
  //   <Table>
  //     <TableHead>
  //       <TableRow>
  //         <TableCell key="module-id">Module ID</TableCell>
  //         <TableCell key="module-name">Module Name</TableCell>
  //         <TableCell key="remove">Remove</TableCell>
  //       </TableRow>
  //     </TableHead>
  //     {moduleList.length > 0 && (
  //       <TableBody>
  //         {moduleList.map(({ moduleId, moduleName }) => (
  //           <TableRow key={moduleId} hover tabIndex={-1}>
  //             <TableCell key={"id:" + moduleId}>{moduleId}</TableCell>
  //             <TableCell key={"name:" + moduleId}>{moduleName}</TableCell>
  //             <TableCell key={"remove:" + moduleId}>
  //               <IconButton onClick={() => handleRemoveModule(moduleId)}>
  //                 <DeleteIcon />
  //               </IconButton>
  //             </TableCell>
  //           </TableRow>
  //         ))}
  //       </TableBody>
  //     )}
  //   </Table>
  // </TableContainer>
  //     </DialogContent>
  //     <DialogActions>
  //       <Button onClick={handleClose} color="secondary">
  //         Cancel
  //       </Button>
  //       <Button
  //         onClick={handleApply}
  //         disabled={
  //           moduleList.length === 0 || moduleGroup.moduleGroupName === ""
  //             ? true
  //             : false
  //         }
  //       >
  //         Apply
  //       </Button>
  //     </DialogActions>
  //   </Dialog>
  // );
};

export default NewModuleGroupDialog;
